/*
 * ikigaino. — theme vanilla JS
 * Scroll reveal, cart open, AJAX add, qty stepper, variant picker.
 * No jQuery, no GSAP. React lives only in islands/.
 */
(function () {
  'use strict';

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveal ---------------------------------- */
  let revealIO = null;
  function initReveal() {
    if (reducedMotion) {
      document.querySelectorAll('[data-reveal]').forEach((n) => n.classList.add('is-in'));
      return;
    }
    revealIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            const delay = (parseInt(el.getAttribute('data-reveal-delay') || '0', 10) + i * 140);
            el.style.transitionDelay = delay + 'ms';
            el.classList.add('is-in');
          } else {
            // AOS-style mirror: fade back out when the element leaves the
            // viewport so the entrance replays on the next scroll past.
            el.style.transitionDelay = '0ms';
            el.classList.remove('is-in');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => revealIO.observe(el));
  }

  /**
   * Register newly-inserted [data-reveal] elements (e.g. after AJAX filter
   * update) with the existing IntersectionObserver. No-op if reduced-motion
   * or observer not yet initialized.
   */
  function observeReveals(root) {
    if (!revealIO) return;
    const scope = root || document;
    scope.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => revealIO.observe(el));
  }
  window.__igObserveReveals = observeReveals;

  /* ---------- Cart drawer open ------------------------------- */
  function initCartOpener() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cart-toggle]');
      if (!trigger) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('ikigaino:cart:update', { detail: { open: true } }));
    });
  }

  /* ---------- Add-to-cart AJAX ------------------------------- */
  function initAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form.matches('form[action$="/cart/add"]')) return;
      // Dawn's <product-form> (quick-add cards, Dawn main-product) already binds
      // its own submit handler that POSTs /cart/add — skip those so the item is
      // not added twice. ikigaino's own forms (PDP, product cards) are plain forms.
      if (form.closest('product-form')) return;
      e.preventDefault();
      const fd = new FormData(form);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(await res.text());
        window.dispatchEvent(new CustomEvent('ikigaino:cart:update', { detail: { open: true } }));
        const errBox = form.querySelector('[data-atc-error]');
        if (errBox) { errBox.hidden = true; errBox.textContent = ''; }
      } catch (err) {
        console.error('[ikigaino] add to cart failed', err);
        const errBox = form.querySelector('[data-atc-error]');
        if (errBox) {
          errBox.textContent = 'Sorry, that item could not be added. Please try again.';
          errBox.hidden = false;
        }
      }
    });
  }

  /* ---------- Quantity stepper ------------------------------- */
  function initQtyStepper() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-inc], [data-qty-dec]');
      if (!btn) return;
      e.preventDefault();
      const wrap = btn.parentElement;
      const input = wrap.querySelector('[data-qty-input]') || wrap.querySelector('input[type="number"]');
      if (!input) return;
      const current = Math.max(1, parseInt(input.value || '1', 10));
      const next = btn.hasAttribute('data-qty-inc') ? current + 1 : Math.max(1, current - 1);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Sticky PDP ATC (mobile) ------------------------ */
  function initStickyATC() {
    const stickyBar = document.querySelector('[data-pdp-sticky]');
    const mainAtc = document.querySelector('.ig-pdp__atc');
    const form = document.querySelector('[data-pdp-form]');
    if (!stickyBar || !mainAtc || !form) return;

    stickyBar.hidden = false;
    document.body.classList.add('has-sticky-atc');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
          // Mirrored on <body> so the floating back-to-top can dodge the bar.
          document.body.classList.toggle('has-sticky-atc', !entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    io.observe(mainAtc);

    const stickyBtn = stickyBar.querySelector('[data-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // Submit the main form (variant/qty state already mirrored to hidden inputs)
        form.requestSubmit();
      });
    }

    // Mirror ATC state (price + disabled) from main ATC to sticky bar
    const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
    const mainLabel = form.querySelector('[data-atc-label]');
    const priceCurrent = form.parentElement?.querySelector('.ig-pdp__price-current');
    const observer = new MutationObserver(() => {
      if (stickyBtn) {
        stickyBtn.toggleAttribute('disabled', mainAtc.hasAttribute('disabled'));
        const labelText = (mainLabel?.textContent || '').split('—')[0].trim();
        if (labelText) stickyBtn.textContent = labelText;
      }
      if (stickyPrice && priceCurrent) {
        stickyPrice.textContent = priceCurrent.textContent.trim();
      }
    });
    observer.observe(mainAtc, { attributes: true, attributeFilter: ['disabled'] });
    if (mainLabel) observer.observe(mainLabel, { childList: true, characterData: true, subtree: true });
    if (priceCurrent) observer.observe(priceCurrent, { childList: true, characterData: true });
  }

  /* ---------- Shipping ETA (client-side, cache-safe) --------- */
  function initShippingETA() {
    const el = document.querySelector('[data-ship-eta]');
    if (!el) return;
    const processDays = parseInt(el.getAttribute('data-process-days') || '3', 10);
    const cutoffHour = parseInt(el.getAttribute('data-cutoff-hour') || '14', 10);

    const now = new Date();
    let start = new Date(now);
    if (now.getHours() >= cutoffHour) start.setDate(start.getDate() + 1);

    // Advance by process days, skipping Sat (6) / Sun (0)
    let added = 0;
    while (added < processDays) {
      start.setDate(start.getDate() + 1);
      const day = start.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    const fmt = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short' });
    el.textContent = fmt.format(start);
  }

  /* ---------- KV hero carousel (journal homepage) ------------ */
  function initHeroCarousel() {
    const media = document.querySelector('[data-hero-carousel]');
    if (!media || reducedMotion) return;
    const slides = media.querySelectorAll('.ig-hero__slide');
    if (slides.length < 2) return;
    const interval = Math.max(3, parseInt(media.getAttribute('data-hero-interval') || '6', 10)) * 1000;
    // Slides 2..n ship with data-src/data-srcset so hidden slides cost no
    // bandwidth up front (and reduced-motion users, who never rotate, pay
    // nothing). Each slide is hydrated one interval before it shows.
    // Document order guarantees <source> hydrates before its <img>.
    const hydrate = (slide) => {
      slide.querySelectorAll('[data-srcset], [data-src]').forEach((el) => {
        if (el.dataset.srcset) { el.srcset = el.dataset.srcset; delete el.dataset.srcset; }
        if (el.dataset.src) { el.src = el.dataset.src; delete el.dataset.src; }
      });
    };
    media.__igHydrateSlide = hydrate;
    let idx = 0;
    hydrate(slides[1]);
    setInterval(() => {
      slides[idx].classList.remove('is-active');
      idx = (idx + 1) % slides.length;
      hydrate(slides[(idx + 1) % slides.length]);
      slides[idx].classList.add('is-active');
    }, interval);
  }

  /* ---------- Reel strips (V11 PLP / PDP) --------------------- */
  /* Per horizontal strip: live "01 — 04 / 08" progress, prev/next arrows and
     mouse drag-to-scroll (wheel stays native vertical — never hijacked).
     Safe to call again after AJAX swaps — groups carry data-reel-init. */
  function initReels(scope) {
    (scope || document).querySelectorAll('[data-reel-group]').forEach((group) => {
      if (group.dataset.reelInit) return;
      group.dataset.reelInit = '1';
      const strip = group.querySelector('[data-reel-strip]');
      if (!strip) return;
      const prog = group.querySelector('[data-reel-progress]');
      const prev = group.querySelector('[data-reel-prev]');
      const next = group.querySelector('[data-reel-next]');
      const pad2 = (n) => String(n).padStart(2, '0');

      // Tunables. The wheel bands are coupled: a Firefox line-mode notch is
      // deltaY 3 x LINE_PX = 48px — which is why notches are detected by
      // deltaMode first, magnitude second.
      const DRAG_PX = 6; // pointer movement before a drag engages
      const NOTCH_MIN = 50; // px delta treated as a discrete mouse notch
      const FLICK_MIN = 240; // px delta treated as a hard flick (falls through)
      const LINE_PX = 16; // px per line for deltaMode 1 (Firefox wheels)
      const SETTLE_EPS = 0.6; // px remaining = glide done
      const SNAP_RESTORE_MS = 160; // idle before scroll-snap re-engages
      const EASE = 0.84; // fraction of remaining distance kept per 16.7ms

      // Layout caches — style/layout reads per scroll frame force reflows
      // mid-glide, so gap is cached and refreshed on resize.
      let gapCache = -1;
      const gapW = () => {
        if (gapCache < 0) gapCache = parseFloat(getComputedStyle(strip).columnGap) || 0;
        return gapCache;
      };
      const stepW = () => {
        const item = strip.firstElementChild;
        if (!item) return 0;
        return item.getBoundingClientRect().width + gapW();
      };
      const maxScroll = () => strip.scrollWidth - strip.clientWidth;

      let lastProg = '';
      const update = () => {
        const total = strip.children.length;
        const step = stepW();
        if (!total || step <= 0) return;
        if (prev) prev.toggleAttribute('disabled', strip.scrollLeft <= 2);
        if (next) next.toggleAttribute('disabled', strip.scrollLeft >= maxScroll() - 2);
        if (prog) {
          const first = Math.min(total, Math.round(strip.scrollLeft / step) + 1);
          const visible = Math.max(1, Math.floor((strip.clientWidth + gapW()) / step));
          const last = Math.min(total, first + visible - 1);
          const text = `${pad2(first)} — ${pad2(last)} / ${pad2(total)}`;
          if (text !== lastProg) { lastProg = text; prog.textContent = text; }
        }
      };

      strip.addEventListener('scroll', update, { passive: true });
      const onResize = () => {
        if (!strip.isConnected) { window.removeEventListener('resize', onResize); return; }
        gapCache = -1;
        update();
      };
      window.addEventListener('resize', onResize);

      /* ---- shared gesture state ---- */
      let dragging = false; // true only after DRAG_PX of movement
      let dragArmed = false; // pointer down, not yet a drag
      let startX = 0;
      let startLeft = 0;
      let moved = 0;
      let wheelIdle = null;
      let wheelTarget = null;
      let wheelPos = 0; // float mirror — scrollLeft reads back integer-rounded
      let wheelRaf = 0;
      let wheelPrevT = 0;

      // keepClass: a pointerdown cancel keeps .is-wheeling on (snap stays
      // suspended) until the press ends — re-snapping mid-press yanks the
      // strip under the cursor and retargets the click.
      const wheelCancel = (keepClass) => {
        wheelTarget = null;
        wheelPrevT = 0;
        if (wheelRaf) { cancelAnimationFrame(wheelRaf); wheelRaf = 0; }
        clearTimeout(wheelIdle);
        if (!keepClass) wheelIdle = setTimeout(() => strip.classList.remove('is-wheeling'), SNAP_RESTORE_MS);
      };
      const wheelStep = (t) => {
        if (wheelTarget == null) { wheelRaf = 0; return; }
        const diff = wheelTarget - wheelPos;
        if (Math.abs(diff) < SETTLE_EPS) {
          strip.scrollLeft = wheelTarget;
          wheelCancel(false);
          return;
        }
        // Time-based ease-out chase: identical feel on 60Hz and 120Hz.
        const dt = wheelPrevT ? Math.min(t - wheelPrevT, 50) : 16.7;
        wheelPrevT = t;
        wheelPos += diff * (1 - Math.pow(EASE, dt / 16.7));
        strip.scrollLeft = wheelPos;
        wheelRaf = requestAnimationFrame(wheelStep);
      };
      // External scrollers (PDP gallery variant sync etc.) call this before
      // their own smooth scroll so the glide rAF doesn't stomp them.
      strip.__igCancelWheel = wheelCancel;

      // Smart wheel assist: slow vertical wheel glides the strip sideways;
      // discrete notches advance exactly one card; at either end the wheel
      // falls straight through so the page reaches the next reel.
      // Hard flicks (> FLICK_MIN) pass through unless a glide is mid-flight.
      if (!reducedMotion) {
        strip.addEventListener('wheel', (e) => {
          if (e.ctrlKey) return; // pinch-zoom / browser zoom — never hijack
          if (e.deltaMode === 2) return; // page-mode wheels stay native
          if (dragging || dragArmed) { e.preventDefault(); return; } // one writer during presses
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // native horizontal gesture
          const isLineMode = e.deltaMode === 1;
          const dy = isLineMode ? e.deltaY * LINE_PX : e.deltaY;
          const engaged = wheelTarget != null;
          if (!engaged && Math.abs(dy) > FLICK_MIN) return;
          const from = engaged ? wheelTarget : strip.scrollLeft;
          const maxL = maxScroll();
          if ((dy > 0 && from >= maxL - 2) || (dy < 0 && from <= 2)) {
            // At the end: swallow only while a glide is still in flight,
            // then hand off to the page immediately.
            if (engaged) { e.preventDefault(); return; }
            return;
          }
          // Line-mode wheels and big px deltas are discrete notches: glide
          // exactly one card so the landing spot IS a snap boundary
          // (mandatory snap would bounce a half-card move straight back).
          // Touchpads (small px deltas) stay 1:1.
          const isNotch = isLineMode || Math.abs(dy) >= NOTCH_MIN;
          const sw = stepW();
          if (isNotch && sw <= 0) return;
          e.preventDefault();
          const delta = isNotch ? Math.sign(dy) * sw : dy;
          strip.classList.add('is-wheeling');
          clearTimeout(wheelIdle);
          if (!engaged) wheelPos = strip.scrollLeft;
          wheelTarget = Math.max(0, Math.min(from + delta, maxL));
          if (!wheelRaf) wheelRaf = requestAnimationFrame(wheelStep);
        }, { passive: false });
      }

      // Arrows own the scroll while clicked — cancel any in-flight glide so
      // the rAF loop doesn't stomp the browser's smooth scrollBy.
      if (prev) prev.addEventListener('click', () => { wheelCancel(false); strip.scrollBy({ left: -stepW(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', () => { wheelCancel(false); strip.scrollBy({ left: stepW(), behavior: 'smooth' }); });

      // Mouse drag-to-scroll — makes the "DRAG →" hint real for desktop users.
      // Touch keeps native swipe; a drag past DRAG_PX suppresses the
      // accidental card click on release.
      strip.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        wheelCancel(true); // stop any glide; keep snap suspended while pressed
        dragArmed = true;
        moved = 0;
        startX = e.clientX;
        startLeft = strip.scrollLeft;
        // No pointer capture yet — capturing at pointerdown retargets plain
        // clicks away from the product links. Capture once a real drag starts.
      });
      strip.addEventListener('pointermove', (e) => {
        if (!dragArmed && !dragging) return;
        // Button already released (it happened outside the strip, before any
        // capture) — disarm instead of ghost-dragging on a bare hover.
        if (!dragging && e.buttons !== 1) { dragArmed = false; return; }
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        if (!dragging) {
          if (moved <= DRAG_PX) return;
          dragging = true;
          strip.classList.add('is-dragging');
          strip.setPointerCapture(e.pointerId);
        }
        strip.scrollLeft = startLeft - dx;
      });
      const endDrag = () => {
        dragArmed = false;
        // Restore snap after the press (a pointerdown glide-cancel kept
        // .is-wheeling on); delayed so the click retarget race can't happen.
        clearTimeout(wheelIdle);
        wheelIdle = setTimeout(() => strip.classList.remove('is-wheeling'), SNAP_RESTORE_MS);
        if (!dragging) return;
        dragging = false;
        strip.classList.remove('is-dragging');
        // pointercancel has no click to consume the suppressor state — reset
        // it after the (possible) click that follows pointerup has been seen.
        setTimeout(() => { moved = 0; }, 0);
      };
      strip.addEventListener('pointerup', endDrag);
      strip.addEventListener('pointercancel', endDrag);
      strip.addEventListener('click', (e) => {
        if (moved > DRAG_PX) {
          e.preventDefault();
          e.stopPropagation();
          moved = 0;
        }
      }, true);

      update();
    });
  }
  window.__igInitReels = initReels;

  /* ---------- Collection filter AJAX ------------------------- */
  let applyFiltersFn = null;
  function initCollectionFilter() {
    const form = document.querySelector('[data-collection-filter-form]');
    const results = document.querySelector('[data-collection-results]');

    if (form && results) {
      applyFiltersFn = async function applyFilters(url) {
        results.classList.add('is-loading');
        try {
          const res = await fetch(url, { headers: { Accept: 'text/html' } });
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.querySelector('[data-collection-results]');
          if (incoming) {
            results.innerHTML = incoming.innerHTML;
            // Re-observe new grid items for reveal (proper IO re-registration)
            observeReveals(results);
            initReels(results);
            paintCardDots(results);
          }
          history.replaceState({}, '', url);
        } catch (err) {
          console.error('[ikigaino] filter failed', err);
        } finally {
          results.classList.remove('is-loading');
        }
      }

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const base = window.location.pathname;
        const fd = new FormData(form);
        const params = new URLSearchParams();
        for (const [key, value] of fd.entries()) {
          if (value !== '' && value !== null) params.append(key, value);
        }
        // Keep the active sort when re-filtering
        const activeSort = new URLSearchParams(window.location.search).get('sort_by');
        if (activeSort) params.set('sort_by', activeSort);
        const qs = params.toString();
        const url = qs ? `${base}?${qs}` : base;
        applyFilters(url);
        form.closest('details')?.removeAttribute('open');
      });
    }

    // Shopify-native sort — swap sort_by on the current URL, keep filters.
    // Wired outside the form guard: the sort select must keep working when
    // the merchant hides the filters (no [data-collection-filter-form]).
    document.querySelectorAll('[data-sort-select]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        if (applyFiltersFn) {
          applyFiltersFn(url.pathname + url.search);
        } else {
          window.location.assign(url.pathname + url.search);
        }
      });
    });
  }

  /* ---------- Predictive search ------------------------------ */
  function initPredictiveSearch() {
    const forms = document.querySelectorAll('[data-predictive-search]');
    forms.forEach((form) => {
      const input = form.querySelector('[data-predictive-input]');
      const results = form.querySelector('[data-predictive-results]');
      if (!input || !results) return;

      let debounce = null;
      let latest = 0;
      let controller = null;
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const safeUrl = (url) => {
        try {
          const u = new URL(String(url), location.origin);
          return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : '#';
        } catch (_e) { return '#'; }
      };

      const close = () => {
        results.hidden = true;
        results.innerHTML = '';
      };

      input.addEventListener('input', () => {
        clearTimeout(debounce);
        const q = input.value.trim();
        if (q.length < 2) { close(); return; }
        debounce = setTimeout(async () => {
          if (controller) controller.abort();
          controller = new AbortController();
          const token = ++latest;
          try {
            const params = new URLSearchParams({
              'q': q,
              'resources[type]': 'product',
              'resources[limit]': '6',
              'resources[options][unavailable_products]': 'last',
            });
            const res = await fetch(`/search/suggest.json?${params.toString()}`, { signal: controller.signal });
            if (token !== latest) return;
            const data = await res.json();
            const products = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (products.length === 0) {
              results.innerHTML = `<div class="ig-search__empty">No matches for "${esc(q)}"</div>`;
              results.hidden = false;
              return;
            }
            results.innerHTML = '<ul class="ig-search__list">' + products.map((p) => `
              <li>
                <a href="${safeUrl(p.url)}" class="ig-search__item">
                  ${p.featured_image && p.featured_image.url ? `<img src="${safeUrl(p.featured_image.url)}" alt="${esc(p.title)}" loading="lazy" />` : '<span class="ig-search__thumb"></span>'}
                  <span class="ig-search__meta">
                    <span class="ig-search__vendor">${esc(p.vendor || '')}</span>
                    <span class="ig-search__title">${esc(p.title)}</span>
                    <span class="ig-search__price">${esc(p.price || '')}</span>
                  </span>
                </a>
              </li>`).join('') + '</ul>';
            results.hidden = false;
          } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[ikigaino] predictive search failed', err);
          }
        }, 160);
      });

      input.addEventListener('blur', () => setTimeout(close, 180));
      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && results.innerHTML) results.hidden = false;
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    });
  }

  /* ---------- Mobile nav (focus trap + a11y) ---------------- */
  function initMobileNav() {
    const drawer = document.getElementById('IgMobileNav');
    if (!drawer) return;
    const panel = drawer.querySelector('.ig-mobile-nav__panel');
    let lastFocus = null;
    const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const trapFocus = (e) => {
      if (e.key !== 'Tab' || !panel) return;
      const f = panel.querySelectorAll(focusableSel);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
    };

    const openDrawer = () => {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (panel) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
      }
      document.body.style.overflow = 'hidden';
      const btn = document.querySelector('.ig-header__menu-btn');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      const firstFocusable = panel && panel.querySelector(focusableSel);
      if (firstFocusable) firstFocusable.focus();
      drawer.addEventListener('keydown', trapFocus);
    };

    const closeDrawer = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      const btn = document.querySelector('.ig-header__menu-btn');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      drawer.removeEventListener('keydown', trapFocus);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    };

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-menu-toggle]');
      if (!trigger) return;
      e.preventDefault();
      if (drawer.classList.contains('is-open')) closeDrawer(); else openDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
  }

  /* ---------- Filter clear ----------------------------------- */
  function initFilterClear() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-filter-clear]');
      if (!btn) return;
      e.preventDefault();
      const form = btn.closest('form');
      if (form) {
        form.querySelectorAll('input[type="checkbox"]').forEach((c) => (c.checked = false));
        form.querySelectorAll('input[type="number"], input[type="text"], input[type="search"]').forEach((i) => (i.value = ''));
      }
      if (applyFiltersFn) {
        applyFiltersFn(window.location.pathname);
      } else {
        window.location.assign(window.location.pathname);
      }
    });
  }

  /* ---------- Color swatch resolver -------------------------- */
  // Baseline CSS named-color map; merchant can override via theme setting `ikigaino_color_map`.
  const COLOR_MAP = {
    // --- basics ---
    'white': '#ffffff', 'cream': '#f5f0e4', 'beige': '#e4d9c3', 'tan': '#d2b48c',
    'brown': '#8b5a2b', 'walnut': '#5d4037', 'black': '#161616', 'charcoal': '#414145',
    'grey': '#888', 'gray': '#888', 'silver': '#c0c0c0',
    // --- reds / pinks ---
    'red': '#ed4545', 'coral': '#ed6a47', 'pink': '#f5c4c0', 'rose': '#e6a4b0',
    'ruby': '#e0115f', 'blush': '#de5d83', 'fuchsia': '#ff00ff', 'magenta': '#ff00ff',
    'burgundy': '#800020', 'maroon': '#800000', 'oxblood': '#4a0000',
    // --- oranges / yellows ---
    'orange': '#e8a668', 'yellow': '#f4f46e', 'mustard': '#d6a82b', 'gold': '#d4a373',
    'amber': '#ffbf00', 'lemon': '#fff44f', 'peach': '#ffcba4',
    'rust': '#b7410e', 'terracotta': '#e2725b', 'mahogany': '#c04000',
    // --- greens ---
    'green': '#3f9e5a', 'sage': '#889387', 'olive': '#6b7c4f', 'mint': '#b8d4c5',
    'lime': '#32cd32', 'emerald': '#50c878', 'jade': '#00a86b', 'forest': '#228b22',
    // --- blues ---
    'teal': '#36baa0', 'blue': '#4a7aa8', 'navy': '#1f2d4a', 'sky': '#a8c8d9', 'turquoise': '#40a9b0',
    'aqua': '#00ffff', 'cobalt': '#0047ab', 'cerulean': '#007ba7', 'sapphire': '#0f52ba',
    'midnight': '#191970',
    // --- purples ---
    'purple': '#7a5ca8', 'violet': '#8a5ea8', 'lavender': '#b8a5cc',
    'indigo': '#4b0082', 'plum': '#8e4585', 'mauve': '#e0b0ff',
    // --- warm / neutral tones ---
    'warm': '#fdf4dc', 'golden': '#d4a373', 'nude': '#e3bc9a', 'champagne': '#f7e7ce',
    'sand': '#c2b280', 'camel': '#c19a6b', 'oatmeal': '#d8caaf', 'khaki': '#c3b091',
    'taupe': '#483c32', 'stone': '#928e85', 'ivory': '#fffff0', 'natural': '#e4d9c3',
    // --- dark / rich tones ---
    'slate': '#708090', 'espresso': '#3c1414', 'mocha': '#967969',
    'ash': '#b2beb5', 'smoke': '#738276',
    // --- materials / finishes ---
    'wooden': '#8b5a2b', 'chrome': '#c0c0c0', 'brass': '#b5a642',
    'copper': '#b87333', 'bronze': '#cd7f32',
    'hyaline': '#e8e8e8', 'clear': '#e8e8e8', 'transparent': '#e8e8e8', 'crystal': '#e8e8e8',
  };
  // --- multi (one wheel shared by every alias) ---
  const MULTI_WHEEL = 'conic-gradient(#ed6a47, #f4f46e, #3f9e5a, #4a7aa8, #7a5ca8, #ed6a47)';
  ['multicolour', 'multicolor', 'rainbow', 'colorful', 'colourful', 'mixed'].forEach((k) => {
    COLOR_MAP[k] = MULTI_WHEEL;
  });

  function resolveColor(name) {
    if (!name) return null;
    const n = String(name).toLowerCase().trim();
    if (COLOR_MAP[n]) return COLOR_MAP[n];
    // Try each word right-to-left (e.g. "8000K Crystal Blue" → blue)
    const words = n.split(/[\s\-\/_]+/).filter(Boolean);
    for (let i = words.length - 1; i >= 0; i--) {
      if (COLOR_MAP[words[i]]) return COLOR_MAP[words[i]];
    }
    // CSS hex / rgb / hsl passes through
    if (/^#[0-9a-f]{3,8}$/i.test(n) || /^(rgb|hsl)/i.test(n)) return n;
    return null;
  }

  function paintSwatchDots(root) {
    root.querySelectorAll('.ig-pdp__swatch-dot').forEach((dot) => {
      // The whole option value arrives via data-cname; the split happens here.
      let c1 = null;
      let c2 = null;
      const raw = dot.getAttribute('data-cname') || '';
      const halves = raw.split('/').map((s) => s.trim()).filter(Boolean);
      if (halves.length >= 2) {
        c1 = resolveColor(halves[0]);
        c2 = resolveColor(halves[1]);
      } else {
        // "White Blue" style duals: both words must resolve; otherwise
        // treat the whole string as one name (right-to-left word scan
        // inside resolveColor finds e.g. the Rainbow in
        // "N Mechanical Rainbow").
        const words = raw.trim().split(/\s+/);
        if (words.length === 2) {
          const a = resolveColor(words[0]);
          const b = resolveColor(words[1]);
          if (a && b) { c1 = a; c2 = b; }
        }
        if (c1 === null && c2 === null) c1 = resolveColor(raw);
      }
      const btn = dot.closest('.ig-pdp__swatch');
      if (c1 === null && c2 === null) {
        // No color resolved — demote to text button; the marker lets
        // applyImageSwatchFallback upgrade it to a variant-image thumb.
        dot.remove();
        const hiddenText = btn && btn.querySelector('.visually-hidden');
        if (hiddenText) hiddenText.classList.remove('visually-hidden');
        if (btn) {
          btn.classList.remove('is-color');
          btn.dataset.colorMiss = '1';
        }
        return;
      }
      const a = c1 || c2 || '#dcdcdc';
      const b = c2 || c1 || a;
      // A gradient can't sit inside another gradient — if either half is a
      // gradient (Rainbow/Multicolour aliases), it takes the whole dot.
      const grad = [a, b].find((v) => String(v).indexOf('gradient') > -1);
      if (grad || a === b) {
        dot.style.background = grad || a;
      } else {
        dot.style.background = `linear-gradient(90deg, ${a} 50%, ${b} 50%)`;
      }
    });
  }

  /* ---------- Variant picker (swatch buttons) ---------------- */
  function initVariantPicker() {
    document.querySelectorAll('.ig-pdp__variants').forEach((root) => {
      const form = root.closest('[data-pdp-form]');
      if (!form) return;
      const hiddenId = form.querySelector('[data-variant-id]');
      const atcLabel = form.querySelector('[data-atc-label]');
      const atcPrice = form.querySelector('[data-atc-price]');
      const atcSep = form.querySelector('[data-atc-sep]');
      const atcBtn = form.querySelector('[data-atc-btn]');
      const availStatus = form.parentElement?.querySelector('[data-pdp-availability] .ig-pdp__avail-status');
      const invPill = document.querySelector('[data-pdp-inv-pill]');

      let variants = [];
      try {
        variants = JSON.parse(root.getAttribute('data-variants') || '[]');
      } catch (err) {
        console.error('[ikigaino] variants parse failed', err);
        return;
      }

      const optionGroups = root.querySelectorAll('[data-option-index]');

      function getSelection() {
        const picks = [];
        optionGroups.forEach((group) => {
          const idx = parseInt(group.dataset.optionIndex, 10);
          const selected = group.querySelector('.ig-pdp__swatch.is-selected');
          if (selected) picks[idx] = selected.dataset.optionValue;
        });
        return picks;
      }

      function findVariant(picks) {
        return variants.find((v) => picks.every((val, idx) => v['option' + (idx + 1)] === val));
      }

      function formatMoney(cents) {
        const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'SGD';
        const locale = document.documentElement.lang || undefined;
        try {
          // No maximumFractionDigits override — Intl uses the currency's own
          // precision (2 for SGD/USD/EUR, 0 for JPY) so it matches Shopify | money.
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        } catch {
          return (cents / 100).toFixed(2) + ' ' + currency;
        }
      }

      function computeStockState(variant) {
        // `stock` is precomputed in Liquid (per variant: 'in' | 'low' | 'out')
        // because `product.variants | json` omits inventory_quantity, so the
        // low-stock threshold can only be evaluated server-side. Fall back to
        // available-only if the field is missing.
        if (variant.stock === 'in' || variant.stock === 'low' || variant.stock === 'out') {
          return variant.stock;
        }
        return variant.available ? 'in' : 'out';
      }

      function writeAvailStatus(el, state, label) {
        while (el.firstChild) el.removeChild(el.firstChild);
        const dot = document.createElement('span');
        dot.className = 'ig-pdp__dot';
        el.appendChild(dot);
        el.appendChild(document.createTextNode(' ' + label));
      }

      function sync() {
        // Update the "COLOUR: Sand" labels first so partial picks still reflect
        optionGroups.forEach((group) => {
          const idx = group.dataset.optionIndex;
          const selected = group.querySelector('.ig-pdp__swatch.is-selected');
          const label = form.parentElement?.querySelector(`[data-option-selected="${idx}"]`);
          if (label && selected) label.textContent = selected.dataset.optionValue;
        });
        const picks = getSelection();
        // Even a partial pick aims the gallery: colourways share photos on
        // dropship listings, so the first variant matching the options picked
        // so far carries the right image — the customer sees their colour
        // immediately, before the remaining options are chosen.
        if (userPicked && picks.some(Boolean)) {
          const partialMatch = findVariant(picks);
          if (partialMatch && partialMatch.image_position) {
            window.dispatchEvent(new CustomEvent('ikigaino:gallery:scrollTo', {
              detail: { index: partialMatch.image_position - 1 },
            }));
          }
        }
        // picks is sparse (holes for untouched option groups); every() skips
        // holes, so an incomplete pick would silently match the first variant.
        // Gate on completeness before resolving.
        const complete = Object.keys(picks).length === optionGroups.length;
        if (!complete) {
          if (hiddenId) hiddenId.value = '';
          if (atcBtn) atcBtn.setAttribute('disabled', '');
          if (atcLabel) atcLabel.textContent = 'Select options';
          if (atcPrice) atcPrice.textContent = '';
          if (atcSep) atcSep.hidden = true;
          return;
        }
        const variant = findVariant(picks);
        if (!variant) {
          if (atcBtn) atcBtn.setAttribute('disabled', '');
          if (atcLabel) atcLabel.textContent = 'Unavailable';
          if (atcPrice) atcPrice.textContent = '';
          if (atcSep) atcSep.hidden = true;
          return;
        }
        if (hiddenId) hiddenId.value = variant.id;
        if (atcBtn) {
          if (variant.available) {
            atcBtn.removeAttribute('disabled');
            if (atcLabel) atcLabel.textContent = 'Add to basket';
            if (atcPrice) atcPrice.textContent = formatMoney(variant.price);
            if (atcSep) atcSep.hidden = false;
          } else {
            atcBtn.setAttribute('disabled', '');
            if (atcLabel) atcLabel.textContent = 'Sold out';
            if (atcPrice) atcPrice.textContent = '';
            if (atcSep) atcSep.hidden = true;
          }
        }
        const stockState = computeStockState(variant);
        const stockLabel = stockState === 'in' ? 'In stock' : stockState === 'low' ? 'Low stock' : 'Sold out';
        if (availStatus) {
          availStatus.classList.toggle('is-in', stockState === 'in');
          availStatus.classList.toggle('is-low', stockState === 'low');
          availStatus.classList.toggle('is-out', stockState === 'out');
          writeAvailStatus(availStatus, stockState, stockLabel);
        }
        if (invPill) {
          const dot = invPill.querySelector('.ig-inv-dot');
          const text = invPill.querySelector('span:not(.ig-inv-dot)');
          if (dot) {
            dot.classList.toggle('ig-inv-dot--in', stockState === 'in');
            dot.classList.toggle('ig-inv-dot--low', stockState === 'low');
            dot.classList.toggle('ig-inv-dot--out', stockState === 'out');
          }
          if (text) text.textContent = stockLabel;
        }
        // Also update price display
        const priceCurrent = form.parentElement?.querySelector('.ig-pdp__price-current');
        if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        // Gallery jump already fired above on the partial pick — the full
        // variant resolves to the same image, no second dispatch needed.
      }

      function selectSwatch(btn) {
        const group = btn.closest('[data-option-index]');
        if (!group) return;
        group.querySelectorAll('.ig-pdp__swatch').forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
          b.setAttribute('tabindex', '-1');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');
        btn.setAttribute('tabindex', '0');
        btn.focus();
        sync();
      }

      let userPicked = false;
      root.addEventListener('click', (e) => {
        const btn = e.target.closest('.ig-pdp__swatch');
        if (!btn) return;
        userPicked = true;
        selectSwatch(btn);
      });

      // Keyboard nav per WAI-ARIA radiogroup pattern
      root.addEventListener('keydown', (e) => {
        const btn = e.target.closest('.ig-pdp__swatch');
        if (!btn) return;
        const group = btn.closest('[data-option-index]');
        if (!group) return;
        const swatches = Array.from(group.querySelectorAll('.ig-pdp__swatch'));
        const idx = swatches.indexOf(btn);
        let next = null;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = swatches[(idx + 1) % swatches.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = swatches[(idx - 1 + swatches.length) % swatches.length];
            break;
          case 'Home':
            next = swatches[0];
            break;
          case 'End':
            next = swatches[swatches.length - 1];
            break;
          case ' ':
          case 'Enter':
            e.preventDefault();
            userPicked = true;
            selectSwatch(btn);
            return;
          default:
            return;
        }
        if (next) { e.preventDefault(); userPicked = true; selectSwatch(next); }
      });

      paintSwatchDots(root);

      // Colour names that resolve to nothing ("N Anlansk", "halfsphere"…)
      // become variant-IMAGE swatches: the option's own product photo says
      // what it is better than any guessed colour could.
      root.querySelectorAll('[data-option-index]').forEach((group) => {
        const optIdx = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('.ig-pdp__swatch[data-color-miss]').forEach((btn) => {
          const value = btn.dataset.optionValue;
          const v = variants.find((vv) => vv['option' + (optIdx + 1)] === value && vv.image_position);
          if (!v) return;
          const img = document.querySelectorAll('.ig-pdp__gallery-item img')[v.image_position - 1];
          const src = img && (img.currentSrc || img.src);
          if (!src) return;
          btn.textContent = '';
          const thumb = document.createElement('span');
          thumb.className = 'ig-pdp__swatch-thumb';
          thumb.style.backgroundImage = `url("${src}")`;
          const sr = document.createElement('span');
          sr.className = 'visually-hidden';
          sr.textContent = value;
          btn.append(thumb, sr);
          btn.classList.add('is-thumb');
        });
      });
      sync();
    });
  }

  /* ---------- Analytics events (GA4 + Meta Pixel) ------------ */
  function ga(name, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, params || {}); } catch (e) {}
    }
  }
  function fbq(name, params) {
    if (typeof window.fbq === 'function') {
      try { window.fbq('track', name, params || {}); } catch (e) {}
    }
  }

  function initAnalyticsEvents() {
    const ctx = window.IG_CONTEXT || {};

    // view_item / view_item_list on page load
    if (ctx.template === 'product' && ctx.product) {
      ga('view_item', {
        currency: ctx.product.currency,
        value: ctx.product.price,
        items: [{
          item_id: String(ctx.product.id),
          item_name: ctx.product.title,
          item_brand: ctx.product.vendor,
          price: ctx.product.price,
          quantity: 1,
        }],
      });
      fbq('ViewContent', {
        content_type: 'product',
        content_ids: [String(ctx.product.id)],
        content_name: ctx.product.title,
        value: ctx.product.price,
        currency: ctx.product.currency,
      });
    } else if (ctx.template === 'collection' && ctx.collection) {
      ga('view_item_list', { item_list_id: String(ctx.collection.id), item_list_name: ctx.collection.title });
    } else if (ctx.template && String(ctx.template).includes('search')) {
      ga('search', { search_term: new URL(location.href).searchParams.get('q') || '' });
    }

    // add_to_cart — dispatched by form submit intercept
    window.addEventListener('ikigaino:cart:update', () => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const last = cart.items[cart.items.length - 1];
          if (!last) return;
          const value = last.line_price / 100;
          const currency = cart.currency || 'SGD';
          ga('add_to_cart', {
            currency,
            value,
            items: [{
              item_id: String(last.product_id),
              item_name: last.product_title,
              item_brand: last.vendor,
              price: last.price / 100,
              quantity: last.quantity,
              item_variant: last.variant_title || undefined,
            }],
          });
          fbq('AddToCart', {
            content_type: 'product',
            content_ids: [String(last.product_id)],
            content_name: last.product_title,
            value,
            currency,
          });
        })
        .catch(() => {});
    });

    // begin_checkout on cart drawer checkout click
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href$="/checkout"], a[href*="/checkout?"], .cart-drawer__checkout');
      if (!link) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          ga('begin_checkout', {
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map((it) => ({
              item_id: String(it.product_id),
              item_name: it.product_title,
              quantity: it.quantity,
              price: it.price / 100,
            })),
          });
          fbq('InitiateCheckout', {
            content_type: 'product',
            content_ids: cart.items.map((i) => String(i.product_id)),
            num_items: cart.item_count,
            value: cart.total_price / 100,
            currency: cart.currency,
          });
        })
        .catch(() => {});
    });

    // search click-out tracking
    window.addEventListener('ikigaino:search:select', (e) => {
      ga('select_item', e.detail || {});
    });
  }

  /* ---------- PDP gallery slider ---------------------------- */
  function initPdpGallery() {
    var gallery = document.getElementById('pdp-gallery');
    if (!gallery) return;
    var viewport = gallery.querySelector('.ig-pdp__gallery-viewport');
    var items = gallery.querySelectorAll('.ig-pdp__gallery-item');
    var thumbs = gallery.querySelectorAll('.ig-pdp__gallery-thumb');
    var prev = gallery.querySelector('[data-gallery-prev]');
    var next = gallery.querySelector('[data-gallery-next]');
    if (!viewport || items.length < 2) return;

    // Desktop renders the gallery as a 2-col CSS grid (with first image hero),
    // and arrows + thumbs are hidden via CSS. Skip scroll-based tracking entirely
    // when the viewport is a grid container (i.e. NOT in the mobile horizontal
    // flex/scroll-snap mode).
    function isHorizontalLayout() {
      return getComputedStyle(viewport).display !== 'grid';
    }

    function getActiveIndex() {
      if (!isHorizontalLayout()) return 0;
      var itemWidth = items[0].offsetWidth || viewport.clientWidth || 1;
      return Math.round(viewport.scrollLeft / itemWidth);
    }

    function scrollToIndex(i) {
      var target = items[i];
      if (!target) return;
      // Cancel any in-flight wheel-assist glide (initReels) so its rAF loop
      // doesn't stomp this smooth scroll frame-by-frame.
      if (typeof viewport.__igCancelWheel === 'function') viewport.__igCancelWheel(false);
      viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    function updateActive() {
      var idx = getActiveIndex();
      thumbs.forEach(function (t, i) {
        var active = i === idx;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (prev) prev.toggleAttribute('disabled', idx === 0);
      if (next) next.toggleAttribute('disabled', idx === items.length - 1);
    }

    var rafId = 0;
    viewport.addEventListener('scroll', function () {
      if (!isHorizontalLayout()) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActive);
    }, { passive: true });

    if (prev) prev.addEventListener('click', function () {
      scrollToIndex(Math.max(0, getActiveIndex() - 1));
    });
    if (next) next.addEventListener('click', function () {
      scrollToIndex(Math.min(items.length - 1, getActiveIndex() + 1));
    });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { scrollToIndex(i); });
    });

    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        scrollToIndex(Math.max(0, getActiveIndex() - 1));
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        scrollToIndex(Math.min(items.length - 1, getActiveIndex() + 1));
        e.preventDefault();
      }
    });

    // Variant pick → ring the matching frame. Start-aligned scrolling can
    // leave 2+ frames visible on desktop, so the ring marks which frame is
    // the picked colour — and stays on until another colour is chosen.
    function markCurrent(item) {
      items.forEach(function (it) { it.classList.remove('is-variant-current'); });
      if (item) item.classList.add('is-variant-current');
    }

    window.addEventListener('ikigaino:gallery:scrollTo', function (e) {
      var idx = e.detail && typeof e.detail.index === 'number' ? e.detail.index : -1;
      if (idx < 0 || idx >= items.length) return;
      if (isHorizontalLayout()) {
        scrollToIndex(idx);
        markCurrent(items[idx]);
      } else {
        var hero = items[0];
        var target = items[idx];
        if (hero && target && hero !== target) {
          var heroImg = hero.querySelector('img');
          var targetImg = target.querySelector('img');
          if (heroImg && targetImg) {
            heroImg.src = targetImg.src;
            heroImg.srcset = targetImg.srcset;
            hero.href = target.href;
            hero.setAttribute('data-pswp-width', target.getAttribute('data-pswp-width') || '');
            hero.setAttribute('data-pswp-height', target.getAttribute('data-pswp-height') || '');
          }
        }
      }
    });

    updateActive();
  }

  /* ---------- PDP accordion animation ----------------------- */
  function initPdpAccordions() {
    var accs = document.querySelectorAll('.ig-pdp__accordions details');
    if (!accs.length) return;
    var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var EASE = 'cubic-bezier(.22,.61,.36,1)';
    accs.forEach(function (d) {
      var summary = d.querySelector('summary');
      var body = d.querySelector('.ig-pdp__acc-body');
      if (!summary || !body) return;
      var animating = false;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (animating) return;
        animating = true;
        var duration = reduced ? 0 : 280;
        if (d.open) {
          var h = body.scrollHeight;
          var anim = body.animate(
            [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: duration, easing: EASE }
          );
          anim.onfinish = function () {
            d.open = false;
            body.style.removeProperty('height');
            body.style.removeProperty('opacity');
            animating = false;
          };
        } else {
          d.open = true;
          var freshH = body.scrollHeight;
          var anim2 = body.animate(
            [{ height: '0px', opacity: 0 }, { height: freshH + 'px', opacity: 1 }],
            { duration: duration, easing: EASE }
          );
          anim2.onfinish = function () {
            body.style.removeProperty('height');
            body.style.removeProperty('opacity');
            animating = false;
          };
        }
      });
    });
  }

  /* ---------- Statement band rotator -------------------------- */
  // Crossfades through the band's statement blocks (.is-active toggles the
  // CSS opacity/translate transition). Reduced-motion users keep the first
  // statement static.
  function initStatementRotator() {
    document.querySelectorAll('[data-statement-rotator]').forEach((rot) => {
      if (reducedMotion) return;
      const items = rot.querySelectorAll('.ig-visit__statement');
      if (items.length < 2) return;
      const interval = Math.max(4, parseInt(rot.getAttribute('data-statement-interval') || '6', 10)) * 1000;
      // WCAG 2.2.2 — pause on keyboard focus only. Mouse hover deliberately
      // does NOT pause: the band should read as an endless loop. Listeners
      // sit on the whole band (the rotator itself holds no focusables —
      // the CTA link outside it is the tab stop that lands here).
      const band = rot.closest('.ig-visit') || rot;
      let paused = false;
      band.addEventListener('focusin', () => { paused = true; });
      band.addEventListener('focusout', () => { paused = false; });
      let idx = 0;
      setInterval(() => {
        if (paused) return;
        items[idx].classList.remove('is-active');
        idx = (idx + 1) % items.length;
        items[idx].classList.add('is-active');
      }, interval);
    });
  }

  /* ---------- Shared rAF-throttled scroll subscription -------- */
  function onScrollRaf(update) {
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            update();
          });
        }
      },
      { passive: true }
    );
  }

  /* ---------- Sticky header (non-home top bar) --------------- */
  // Past one viewport-ish of scroll the top bar detaches: scrolling UP
  // slides it down as a fixed bar; scrolling DOWN tucks it away. Back near
  // the top it returns to natural flow. JS owns the flow-reservation padding
  // (a CSS var set on the bar can't be read by parent rules) and bails out
  // entirely while the bar is hidden (journal home desktop).
  function initStickyHeader() {
    const top = document.querySelector('.ig-header__top');
    if (!top) return;
    const header = top.closest('.ig-header') || top.parentElement;
    let topH = top.offsetHeight;
    window.addEventListener('resize', () => {
      topH = top.classList.contains('is-fixed') ? topH : top.offsetHeight;
    });
    const unfix = () => {
      top.classList.remove('is-fixed', 'is-shown');
      header.style.removeProperty('padding-top');
    };
    let lastY = window.scrollY;
    const update = () => {
      const y = window.scrollY;
      const goingUp = y < lastY - 1;
      const goingDown = y > lastY + 1;
      lastY = y;
      if (!top.classList.contains('is-fixed')) topH = top.offsetHeight;
      if (topH === 0) {
        // Bar is display:none (journal home desktop) — never stick it.
        if (top.classList.contains('is-fixed')) unfix();
        return;
      }
      if (y > topH + 320) {
        if (!top.classList.contains('is-fixed')) {
          // Reserve the bar's slot so content doesn't shift when it leaves flow.
          header.style.paddingTop = `${topH}px`;
          top.classList.add('is-fixed');
        }
        if (goingUp) top.classList.add('is-shown');
        else if (goingDown) top.classList.remove('is-shown');
      } else if (y <= topH) {
        unfix();
      } else {
        // Between the bar's own height and the threshold: keep it tucked.
        top.classList.remove('is-shown');
      }
    };
    onScrollRaf(update);
  }

  /* ---------- Back to top (footer band + floating button) ---- */
  const FAB_SHOW_Y = 320; // px scrolled before the floating nav appears

  function initBackToTop() {
    document.querySelectorAll('[data-back-to-top]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
      });
    });
    const fab = document.querySelector('[data-btt-fab]');
    if (!fab) return;
    const update = () => {
      fab.classList.toggle('is-visible', window.scrollY > FAB_SHOW_Y);
    };
    onScrollRaf(update);
    update();
  }

  /* ---------- PLP reel jumper (floating prev/next reel) ------ */
  // On collection pages the floating nav jumps between reel rows instead of
  // going straight to top; ↑ above the first reel falls back to top.
  function initReelJump() {
    const btns = document.querySelectorAll('[data-reel-jump]');
    if (!btns.length) return;
    const reels = () => [...document.querySelectorAll('[data-reel-group]')];
    // "At this reel" must sit beyond the reel's CSS scroll-margin-top (where
    // a jump lands), or Next would keep re-targeting the current reel — so
    // derive it from the CSS instead of hardcoding a second constant.
    const edge = () => {
      const first = reels()[0];
      const margin = first ? parseFloat(getComputedStyle(first).scrollMarginTop) || 0 : 0;
      return margin + 68;
    };
    const behavior = reducedMotion ? 'auto' : 'smooth';
    const nextTarget = () => {
      const e = edge();
      return reels().find((r) => r.getBoundingClientRect().top > e) || null;
    };
    const prevTarget = () => {
      const e = edge();
      const passed = reels().filter((r) => r.getBoundingClientRect().top < -e);
      return passed[passed.length - 1] || null;
    };
    btns.forEach((btn) => {
      const dir = parseInt(btn.getAttribute('data-reel-jump'), 10);
      btn.addEventListener('click', () => {
        if (dir > 0) {
          const t = nextTarget();
          if (t) t.scrollIntoView({ behavior, block: 'start' });
        } else {
          const t = prevTarget();
          if (t) t.scrollIntoView({ behavior, block: 'start' });
          else window.scrollTo({ top: 0, behavior });
        }
      });
    });
    // Disable ↓ at the last reel (↑ always works — it falls back to top).
    // Polled on a slow interval instead of per scroll frame: the check costs
    // N getBoundingClientRect reads, and polling also catches AJAX filter
    // re-renders that swap the reels without any scroll event.
    const down = document.querySelector('[data-reel-jump="1"]');
    if (down) {
      const update = () => down.toggleAttribute('disabled', !nextTarget());
      setInterval(update, 400);
      update();
    }
  }

  /* ---------- Product card colour dots ------------------------ */
  // Cards render one hidden dot per Color option value; only values the
  // shared COLOR_MAP resolves get painted (max 5), the rest are removed.
  // Rows with zero resolvable colours never show.
  function paintCardDots(root) {
    (root || document).querySelectorAll('[data-card-dots]').forEach((row) => {
      if (row.dataset.dotsDone) return;
      row.dataset.dotsDone = '1';
      let shown = 0;
      row.querySelectorAll('.ig-card__dot').forEach((dot) => {
        const c = resolveColor(dot.getAttribute('data-cname'));
        if (!c || shown >= 5) {
          dot.remove();
          return;
        }
        dot.style.background = c;
        shown++;
      });
      // The row itself always stays (even empty) so every card keeps the
      // same meta height; is-painted reveals the dots post-paint (no FOUC).
      row.classList.add('is-painted');
    });
  }

  /* ---------- PDP description groomer -------------------------- */
  // Dropship imports arrive as editorial copy + an "<h1>SPECIFICATIONS</h1>"
  // dump of "Key: Value" paragraphs, empty <p>s and inline colours. Rendered
  // here as a proper spec sheet — the admin HTML stays untouched.
  function initPdpSpecs() {
    document.querySelectorAll('.ig-pdp__desc, .ig-pdp__acc-body').forEach((box) => {
      // Imported descriptions often carry dead image links — drop them
      // instead of showing broken frames.
      box.querySelectorAll('img').forEach((img) => {
        // Dead images error AFTER the empty-wrapper pass ran, so the kill
        // walks up and removes wrappers the image leaves empty.
        const kill = () => {
          let parent = img.parentElement;
          img.remove();
          while (
            parent && parent !== box &&
            !parent.textContent.replace(/\u00a0/g, ' ').trim() &&
            !parent.querySelector('img')
          ) {
            const up = parent.parentElement;
            parent.remove();
            parent = up;
          }
        };
        if (img.complete && img.naturalWidth === 0) kill();
        else img.addEventListener('error', kill);
      });
      // Empty wrappers (dead-image shells etc.) collapse in two passes so
      // parents emptied by the first pass go too.
      for (let pass = 0; pass < 2; pass++) {
        box.querySelectorAll('div, p').forEach((el) => {
          if (!el.textContent.replace(/\u00a0/g, ' ').trim() && !el.querySelector('img')) el.remove();
        });
      }
      box.querySelectorAll('p').forEach((p) => {
        if (!p.textContent.replace(/ /g, ' ').trim() && !p.querySelector('img')) p.remove();
      });
      const specHead = [...box.querySelectorAll('h1, h2, h3')].find((h) => /specification/i.test(h.textContent));
      if (!specHead) return;
      const rows = [];
      const consumed = [];
      let node = specHead.nextElementSibling;
      while (node && !/^H[1-3]$/.test(node.tagName)) {
        const next = node.nextElementSibling;
        const text = node.textContent.replace(/ /g, ' ').trim();
        const m = text.match(/^([^:]{1,40}):\s*(.+)$/);
        if (m) {
          rows.push([m[1].trim(), m[2].trim()]);
          consumed.push(node);
        } else if (!text) {
          consumed.push(node);
        }
        node = next;
      }
      // Junk rows like "Brand Name: NONE" say nothing — drop them.
      const clean = rows.filter((r) => !/^(none|no|n\/a|-)$/i.test(r[1]));
      if (!rows.length) return;
      consumed.forEach((n) => n.remove());
      if (!clean.length) {
        specHead.remove();
        return;
      }
      const pretty = (s) => {
        const t = s.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        return t.charAt(0).toUpperCase() + t.slice(1);
      };
      const title = document.createElement('h3');
      title.className = 'ig-pdp__specs-title';
      title.textContent = 'Specifications';
      const dl = document.createElement('dl');
      dl.className = 'ig-pdp__specs';
      clean.forEach(([k, v]) => {
        const dt = document.createElement('dt');
        dt.textContent = pretty(k);
        const dd = document.createElement('dd');
        dd.textContent = v;
        dl.append(dt, dd);
      });
      specHead.replaceWith(title);
      title.after(dl);
    });
  }

  /* ---------- Theme editor: bring selected blocks into view --- */
  function initEditorBlockSelect() {
    if (!(window.Shopify && Shopify.designMode)) return;
    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest('.ig-hero__slide');
      if (slide) {
        const media = slide.parentElement;
        if (typeof media.__igHydrateSlide === 'function') media.__igHydrateSlide(slide);
        media.querySelectorAll('.ig-hero__slide').forEach((s) => s.classList.remove('is-active'));
        slide.classList.add('is-active');
      }
      const st = e.target.closest('.ig-visit__statement');
      if (st) {
        st.parentElement.querySelectorAll('.ig-visit__statement').forEach((s) => s.classList.remove('is-active'));
        st.classList.add('is-active');
      }
    });
  }

  /* ---------- Init on DOM ready ------------------------------ */
  function start() {
    initReveal();
    initCartOpener();
    initAddToCart();
    initQtyStepper();
    initVariantPicker();
    initFilterClear();
    initMobileNav();
    initPredictiveSearch();
    initCollectionFilter();
    initReels();
    initHeroCarousel();
    initStatementRotator();
    initStickyHeader();
    initBackToTop();
    initReelJump();
    initEditorBlockSelect();
    initStickyATC();
    initShippingETA();
    initAnalyticsEvents();
    initPdpGallery();
    initPdpAccordions();
    initPdpSpecs();
    paintCardDots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
